import React from "react";
import {
  Store,
  MapPin,
  CreditCard,
  FileBadge,
  User,
  Home,
  TrendingUp,
  Filter,
  BarChart3,
  IdCard,
  Calendar,
  Banknote,
} from "lucide-react";
import StatCard from './StatCard';
import { formatVND } from '../../../../utils/format';

export default function MerchantOverview({
  merchantInfo,
  revenueLog,
  summary,
}) {
  const hasRecentTransaction = (logs, days = 7) => {
    if (!logs || logs.length === 0) return false;

    const now = new Date();
    const threshold = new Date();
    threshold.setDate(now.getDate() - days);

    return logs.some((item) => {
      const txDate = new Date(item.date);
      return txDate >= threshold;
    });
  };

  const month = revenueLog?.[0]?.date
    ? `Tháng ${revenueLog[0].date.split("-")[1]}`
    : "";
  const isActive = hasRecentTransaction(revenueLog, 7);
  const peakDay =
    revenueLog && revenueLog.length > 0
      ? revenueLog.reduce((max, item) =>
          (item.total_revenue ?? item.revenue) >
          (max.total_revenue ?? max.revenue)
            ? item
            : max,
        )
      : null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-7 gap-6">
      {/* CỘT TRÁI: THÔNG TIN CỬA HÀNG */}
      <div className="lg:col-span-3 bg-white rounded-3xl border border-slate-100 shadow-md relative overflow-hidden flex flex-col h-full">
        {/* Header */}
        <div className="bg-indigo-100 border-b border-indigo-200 p-5 flex gap-3 justify-between">
          <div className="flex flex-col gap-2.5 pl-4 bg-gra">
            <div className="flex items-center gap-1.5 text-slate-500">
              <Store size={20} strokeWidth={2} className="text-indigo-600" />
              <h2
                className="text-lg font-bold text-slate-800 leading-tight line-clamp-1 mb-1"
                title={merchantInfo?.workspaceName}
              >
                {merchantInfo?.workspaceName || "Tên không xác định"}
              </h2>
            </div>

            <div className="flex items-center gap-1.5 text-slate-500">
              <MapPin size={14} className="shrink-0 text-indigo-500" />
              <p className="text-xs font-medium truncate">
                {merchantInfo?.merchantAddress || "Chưa cập nhật địa chỉ"}
              </p>
            </div>
          </div>

          <div className="flex justify-between items-start">
            <span
              className={`
                  px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border
                  ${
                    isActive
                      ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                      : "bg-slate-100 text-slate-500 border-slate-200"
                  }
                `}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 flex-1 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <InfoBox
              label="Mã cửa hàng"
              icon={<CreditCard size={14} className="text-indigo-500" />}
              value={merchantInfo?.terminalID || "--"}
            />
            <InfoBox
              label="Ngành nghề kinh doanh"
              icon={<FileBadge size={14} className="text-amber-500" />}
              value={merchantInfo?.businessType || "N/A"}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid grid-rows-2 gap-3">
              <InfoBox
                label="Mã số kinh doanh"
                icon={<IdCard size={14} className="text-indigo-500" />}
                value={merchantInfo?.businessRegistrationNumber || "--"}
              />
              <InfoBox
                label="Chủ hộ kinh doanh"
                icon={<User size={16} className="text-indigo-500" />}
                value={merchantInfo?.userName || "--"}
              />
            </div>

            <InfoBox
              label="Địa chỉ chủ hộ"
              icon={<Home size={14} className="text-amber-500" />}
              value={merchantInfo?.userAddress || "N/A"}
            />
          </div>
          <div className="p-3 bg-emerald-50/50 rounded-xl border border-emerald-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                <Banknote size={14} />
              </div>
              <p className="text-[12px] font-bold text-emerald-700 uppercase tracking-widest">
                Tổng doanh thu năm
              </p>
            </div>
            <p className="text-md font-black text-emerald-600 pr-5">
              {formatVND(summary?.revenue_year * 1000)}
            </p>
          </div>
        </div>
      </div>

      {/* CỘT PHẢI: CHỈ SỐ TỔNG QUAN */}
      <div className="lg:col-span-4 grid grid-cols-1 sm:grid-cols-2 gap-6 h-full">
        <StatCard
          title={`Doanh Thu ${month}`}
          value={formatVND(summary.revenue_month * 1000)}
          // trend={revenueLog?.[0] ? `${revenueLog[0].date.split("-")[2]}` : "--"}
          icon={TrendingUp}
          iconColorClass="text-indigo-600"
          iconBgClass="bg-indigo-50"
        />
        <StatCard
          title="Số giao dịch"
          value={`${summary.tx_count_month} GD`}
          subValue="Giao dịch hợp lệ"
          icon={Filter}
          iconColorClass="text-amber-500"
          iconBgClass="bg-amber-50"
        />
        <StatCard
          title="Giá trị TB/Đơn"
          value={formatVND(
            (summary.revenue_month / (summary.tx_count_month || 1)) * 1000,
          )}
          icon={BarChart3}
          iconColorClass="text-emerald-500"
          iconBgClass="bg-emerald-50"
        />
        <StatCard
          title="Ngày cao điểm"
          value={peakDay ? `Ngày ${peakDay.date.split("-")[2]}` : "--"}
          subValue="Doanh thu cao nhất"
          icon={Calendar}
          iconColorClass="text-rose-500"
          iconBgClass="bg-rose-50"
        />
      </div>
    </div>
  );
}

function InfoBox({ label, icon, value }) {
  return (
    <div className="px-2 py-2 bg-slate-50 rounded-xl border border-slate-100">
      <p className="text-[9px] text-slate-400 font-bold uppercase mb-1">
        {label}
      </p>

      <div className="flex items-start gap-1.5 text-xs font-bold text-slate-700">
        {/* Icon cố định, không bị mất */}
        <span className="flex-shrink-0 mt-0.5">{icon}</span>

        {/* Text cho phép xuống dòng */}
        <span className="break-words">{value}</span>
      </div>
    </div>
  );
}
